import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ItemsContainer, SearchContainer } from './styled.bottomSheetSelector';
import { BottomSheet } from '../common/sharedComponents/BottomSheet';
import { SearchField } from '../common/sharedComponents/SearchField';
import { SearchIcon } from '../common/icons/SearchIcon';
import { setItem } from '../../utils/localStorage';
import { IBottomSheetSelectorProps, IHotelData } from '../@types/bottomSheet';
import { BottomSheetHotelList } from './components/BottomSheetHotelList';

function BottomSheetSelector ({
  hotels,
  setIsOpen,
  isOpen,
  selectedHotel,
  setSelectedHotel,
  selectedClient,
  setSelectedClient,
  selectedBrand,
  setSelectedBrand,
  isSearchable = false,
  searchableInputPlaceHolder,
  title,
}: IBottomSheetSelectorProps) {
  const [searchText, setSearchText] = useState<string>('');
  const isClosingRef = useRef<boolean>(false);

  const selectedHotelData = useMemo<IHotelData | null>(
    () =>
      hotels.find(
        (hotel) =>
          hotel.id === selectedHotel &&
          hotel.brandId === selectedBrand &&
          hotel.clientId === selectedClient
      ) || (hotels.length ? hotels[0] : null),
    [hotels, selectedHotel, selectedBrand, selectedClient]
  );

  const filteredHotels = useMemo<IHotelData[]>(() => {
    if (!searchText) {
      return hotels;
    }

    const searchTextLowerCase = searchText.toLowerCase();
    return hotels.filter(
      (hotel) =>
        hotel &&
        (hotel.name.toLowerCase().includes(searchTextLowerCase) ||
          hotel.brandName.toLowerCase().includes(searchTextLowerCase))
    );
  }, [hotels, searchText]);

  const searchWords = useMemo<string[]>(
    () =>
      searchText
        .toLowerCase()
        .split(' ')
        .filter((word) => word),
    [searchText]
  );

  const isItemSelected = useCallback(
    (hotelData: IHotelData): boolean =>
      hotelData.id === selectedHotel &&
      hotelData.brandId === selectedBrand &&
      hotelData.clientId === selectedClient,
    [selectedHotel, selectedBrand, selectedClient]
  );

  const handleHotelSelect = useCallback(
    (hotelData: IHotelData): void => {
      // Prevent further interactions during close
      if (isClosingRef.current) {
        return;
      }
      isClosingRef.current = true;

      // Close bottom sheet first for immediate visual feedback
      setIsOpen(false);
      setSearchText('');

      // Delay state updates until after bottom sheet animation completes (~300ms)
      setTimeout(() => {
        setItem('selectedHotel', hotelData.id);
        setItem('selectedBrand', hotelData.brandId);
        setItem('selectedClient', hotelData.clientId);

        setSelectedHotel(hotelData.id);
        setSelectedBrand(hotelData.brandId);
        setSelectedClient(hotelData.clientId);

        isClosingRef.current = false;
      }, 350);
    },
    [setSelectedHotel, setSelectedBrand, setSelectedClient, setIsOpen]
  );

  const handleClose = useCallback((): void => {
    setIsOpen(false);
    setSearchText('');
  }, [setIsOpen]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setSearchText(e.target.value);
    },
    []
  );

  const handleClearSearch = useCallback((): void => {
    setSearchText('');
  }, []);

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose} title={title}>
      {isSearchable && (
        <SearchContainer>
          <SearchField
            icon={<SearchIcon />}
            placeholderText={searchableInputPlaceHolder || ''}
            textValue={searchText}
            onChange={handleSearchChange}
            onClearTextValue={handleClearSearch}
          />
        </SearchContainer>
      )}
      <ItemsContainer>
        <BottomSheetHotelList
          filteredHotels={filteredHotels}
          selectedHotelData={selectedHotelData}
          searchWords={searchWords}
          isItemSelected={isItemSelected}
          onHotelSelect={handleHotelSelect}
        />
      </ItemsContainer>
    </BottomSheet>
  );
}

export default BottomSheetSelector;
